import palette from './palette'

interface TokenColorMapping {
  [key: string]: string
}

interface ColorTokenProps {
  common: TokenColorMapping
  bg: TokenColorMapping
  border: TokenColorMapping
  content: TokenColorMapping
  text: TokenColorMapping
}

const colorTokens: ColorTokenProps = {
  common: {
    secondary: palette.grey[500],
    tertiary: palette.grey[400],
    dark: palette.grey[900],
    primary: palette.blue[400],
    success: palette.green[500],
    danger: palette.red[500],
    pending: palette.yellow[500],
    white: palette.neutrals['white'],
  },

  text: {
    secondary: palette.grey[300],
    primary: palette.neutrals['white'],
  },
  bg: {
    surfaceAppDark: palette.grey['900'],
    surfaceAppLight: palette.grey['50'],
    surfaceEmphasisDark: palette.grey['600'],
    surfaceEmphasisLight: palette.grey['200'],
    surfacePrimaryDark: palette.grey['700'],
    surfacePrimaryLight: palette.neutrals['white'],
    surfaceSecondaryDark: palette.grey['800'],
    surfaceSecondaryLight: palette.grey['100'],
    surfaceTertiaryDark: palette.blue['900'],
    surfaceTertiaryLight: palette.blue['50'],
    surfaceContrastDark: palette.neutrals['white'],
    surfaceContrastLight: palette.grey['900'],
    statusNeutralBoldDark: palette.grey['400'],
    statusNeutralBoldLight: palette.grey['400'],
    statusNeutralSubtleDark: palette.grey['700'],
    statusNeutralSubtleLight: palette.grey['100'],
    statusDangerBoldDark: palette.red['500'],
    statusDangerBoldLight: palette.red['500'],
    statusDangerDark: palette.red['700'],
    statusDangerLight: palette.red['300'],
    statusDangerSubtleDark: palette.red['900'],
    statusDangerSubtleLight: palette.red['100'],
    statusSuccessBoldDark: palette.green['500'],
    statusSuccessBoldLight: palette.green['500'],
    statusSuccessSubtleDark: palette.green['900'],
    statusSuccessSubtleLight: palette.green['100'],
    statusActiveBoldDark: palette.blue['400'],
    statusActiveBoldLight: palette.blue['400'],
    statusActiveDark: palette.blue['500'],
    statusActiveLight: palette.blue['200'],
    statusActiveSubtleDark: palette.blue['800'],
    statusActiveSubtleLight: palette.blue['100'],
    statusWarningBoldDark: palette.yellow['500'],
    statusWarningBoldLight: palette.yellow['500'],
    statusWarningSubtleDark: palette.yellow['900'],
    statusWarningSubtleLight: palette.yellow['100'],
    statusNeutralDark: palette.grey['600'],
    statusNeutralLight: palette.grey['300'],
    statusSuccessDark: palette.green['700'],
    statusSuccessLight: palette.green['300'],
    statusWarningDark: palette.yellow['600'],
    statusWarningLight: palette.yellow['300'],
    buttonPrimaryDefaultDark: palette.blue['400'],
    buttonPrimaryDefaultLight: palette.blue['400'],
    buttonPrimaryPressedDark: palette.blue['300'],
    buttonSecondaryDefaultDark: palette.blue400['a20'],
    buttonSecondaryDefaultLight: palette.blue['100'],
    buttonSecondaryDefaultPressedDark: palette.blue400['a30'],
    buttonSecondaryDefaultPressedLight: palette.blue['200'],
    buttonTertiaryDefaultDark: palette.white['a5'],
    buttonTertiaryDefaultLight: palette.white['a5'],
    buttonTertiaryPressedDark: palette.white['a10'],
    buttonTertiaryPressedLight: palette.white['a10'],
    buttonTextPrimaryHoverDark: palette.blue400.a10,
    buttonDangerDefaultDark: palette.red['500'],
    buttonDangerDefaultLight: palette.red['500'],
    buttonDangerSecondaryDefaultDark: palette.red['500'],
    buttonDangerSecondaryDefaultLight: palette.red['500'],
    buttonSuccessDefaultDark: palette.green['500'],
    buttonSuccessDefaultLight: palette.green['500'],
  },

  border: {
    statusActiveDark: palette.blue['400'],
    statusActiveLight: palette.blue['400'],
    statusActiveSubtleDark: palette.blue['600'],
    statusActiveSubtleLight: palette.blue['200'],
    statusDangerDark: palette.red['500'],
    statusDangerLight: palette.red['500'],
    statusSuccessDark: palette.green['500'],
    statusSuccessLight: palette.green['500'],
    statusNeutralDark: palette.grey['400'],
    statusNeutralLight: palette.grey['400'],
    contrastDark: palette.neutrals['white'],
    contrastLight: palette.grey['900'],
    surfaceDefaultDark: palette.white['a10'],
    surfaceDefaultLight: palette.black['a10'],
    surfaceEmphasisDark: palette.white['a20'],
    surfaceEmphasisLight: palette.black['a20'],
    buttonOutlinedDefaultDark: palette.blue['400'],
    buttonOutlinedDefaultLight: palette.blue['400'],
  },
  content: {
    primaryDark: palette.neutrals['white'],
    primaryLight: palette.grey['900'],
    secondaryDark: palette.grey['300'],
    secondaryLight: palette.grey['500'],
    tertiaryDark: palette.grey['400'],
    tertiaryLight: palette.grey['400'],
    activeBoldDark: palette.blue['200'],
    activeBoldLight: palette.blue['500'],
    activeDark: palette.blue['400'],
    activeLight: palette.blue['400'],
    dangerBoldDark: palette.red['300'],
    dangerBoldLight: palette.red['600'],
    dangerDark: palette.red['500'],
    dangerLight: palette.red['300'],
    successBoldDark: palette.green['300'],
    successBoldLight: palette.green['600'],
    successDark: palette.green['500'],
    successLight: palette.green['300'],
    warningBoldDark: palette.yellow['200'],
    warningBoldLight: palette.yellow['600'],
    warningDark: palette.yellow['500'],
    warningLight: palette.yellow['300'],
    contrastPrimaryDark: palette.grey['900'],
    contrastPrimaryLight: palette.neutrals['white'],
    decorativeOrangeDark: palette.orange['500'],
    decorativeOrangeLight: palette.orange['500'],
    decorativePurpleBoldDark: palette.purple['300'],
    decorativePurpleBoldLight: palette.purple['300'],
    priceUpDark: palette.green['500'],
    priceUpLight: palette.red['500'],
    priceUpBoldDark: palette.green['300'],
    priceUpBoldLight: palette.red['300'],
    priceDownDark: palette.red['500'],
    priceDownLight: palette.blue['400'],
    priceDownBoldDark: palette.red['300'],
    priceDownBoldLight: palette.blue['200'],
    priceNeutralDark: palette.grey['400'],
    priceNeutralLight: palette.grey['400'],
    priceNeutralBoldDark: palette.grey['300'],
    priceNeutralBoldLight: palette.grey['300'],
  },
}

export default colorTokens
